import { Capacitor } from '@capacitor/core';

let _preferences = null;
let _loadingPromise = null;
let _loadFailed = false;

function withTimeout(promise, ms = 3000) {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Native storage timeout')), ms)
    ),
  ]);
}

/**
 * Lazy-load the @capacitor/preferences plugin. The result is cached and
 * returned synchronously on subsequent calls. We never return the plugin
 * directly from an async function to avoid the Capacitor proxy `.then()` trap.
 */
function getPreferencesSync() {
  return _preferences;
}

async function ensurePreferences() {
  if (_preferences || _loadFailed) return;
  if (!Capacitor.isNativePlatform()) return;
  if (_loadingPromise) { await _loadingPromise; return; }
  _loadingPromise = withTimeout(
    import('@capacitor/preferences').then(mod => { _preferences = mod.Preferences; }),
    3000
  )
    .catch((err) => {
      console.warn('[nativeStorage] Failed to load Preferences plugin:', err);
      _loadFailed = true;
    })
    .finally(() => { _loadingPromise = null; });
  await _loadingPromise;
}

export async function setItem(key, value) {
  await ensurePreferences();
  const Preferences = getPreferencesSync();
  if (Preferences) {
    try {
      await withTimeout(Preferences.set({ key, value }));
      return;
    } catch (err) {
      console.warn('[nativeStorage] Preferences.set failed, using localStorage:', err);
    }
  }
  localStorage.setItem(key, value);
}

export async function getItem(key) {
  await ensurePreferences();
  const Preferences = getPreferencesSync();
  if (Preferences) {
    try {
      const { value } = await withTimeout(Preferences.get({ key }));
      return value;
    } catch (err) {
      console.warn('[nativeStorage] Preferences.get failed, using localStorage:', err);
    }
  }
  return localStorage.getItem(key);
}

export async function removeItem(key) {
  await ensurePreferences();
  const Preferences = getPreferencesSync();
  if (Preferences) {
    try {
      await withTimeout(Preferences.remove({ key }));
      return;
    } catch (err) {
      console.warn('[nativeStorage] Preferences.remove failed, using localStorage:', err);
    }
  }
  localStorage.removeItem(key);
}

// --- Pending Workout Sync Queue ---

const PENDING_WORKOUTS_KEY = 'pendingWorkoutSync';

export async function getPendingWorkouts() {
  const raw = await getItem(PENDING_WORKOUTS_KEY);
  if (!raw) return [];
  try {
    return JSON.parse(raw);
  } catch {
    return [];
  }
}

export async function addPendingWorkout(sessionId, workoutData, options = {}) {
  const { kind = 'regular' } = options;
  const pending = await getPendingWorkouts();
  const idx = pending.findIndex(w => w.sessionId === sessionId);
  if (idx !== -1) {
    // REPLACE the stored payload (never keep a stale one from an earlier, less
    // complete save) and give it a fresh set of sync attempts.
    pending[idx] = {
      ...pending[idx],
      workoutData,
      kind,
      queuedAt: Date.now(),
      attempts: 0,
    };
  } else {
    pending.push({
      sessionId,
      workoutData,
      kind,
      queuedAt: Date.now(),
      attempts: 0,
    });
  }
  await setItem(PENDING_WORKOUTS_KEY, JSON.stringify(pending));
}

export async function removePendingWorkout(sessionId) {
  const pending = await getPendingWorkouts();
  const filtered = pending.filter(w => w.sessionId !== sessionId);
  if (filtered.length === 0) {
    await removeItem(PENDING_WORKOUTS_KEY);
  } else {
    await setItem(PENDING_WORKOUTS_KEY, JSON.stringify(filtered));
  }
}

export async function updatePendingWorkout(sessionId, updates) {
  const pending = await getPendingWorkouts();
  const idx = pending.findIndex(w => w.sessionId === sessionId);
  if (idx === -1) return;
  pending[idx] = { ...pending[idx], ...updates };
  await setItem(PENDING_WORKOUTS_KEY, JSON.stringify(pending));
}

const MAX_SYNC_ATTEMPTS = 5;

// Prevents overlapping sync passes (boot effect + login + dashboard banner can
// all fire near-simultaneously) from double-submitting the same queued workout.
let _syncing = false;

/**
 * Attempt to sync all pending workouts to the server.
 * @param {Function} completeSessionFn - (sessionId, workoutData) => Promise for regular sessions
 * @param {Function} [completeHiitFn]  - (sessionId, workoutData) => Promise for HIIT sessions
 * @param {{ force?: boolean }} [options] - force:true retries even entries that
 *        exhausted MAX_SYNC_ATTEMPTS (user-initiated sync from the dashboard banner).
 * @returns {{ synced: string[], failed: string[] }}
 */
export async function syncPendingWorkouts(completeSessionFn, completeHiitFn, options = {}) {
  const { force = false } = options;
  if (_syncing) return { synced: [], failed: [] };

  const pending = await getPendingWorkouts();
  if (pending.length === 0) return { synced: [], failed: [] };

  _syncing = true;
  const synced = [];
  const failed = [];
  try {
    for (const entry of pending) {
      // After MAX_SYNC_ATTEMPTS, stop AUTO-retrying but NEVER delete the data.
      // The entry stays queued so a later user-initiated (force) sync can flush it.
      if (!force && entry.attempts >= MAX_SYNC_ATTEMPTS) {
        failed.push(entry.sessionId);
        continue;
      }
      try {
        if (entry.kind === 'hiit') {
          if (typeof completeHiitFn !== 'function') {
            // No HIIT handler supplied — keep the entry for a caller that has one.
            failed.push(entry.sessionId);
            continue;
          }
          await completeHiitFn(entry.sessionId, entry.workoutData);
        } else {
          await completeSessionFn(entry.sessionId, entry.workoutData);
        }
        await removePendingWorkout(entry.sessionId);
        synced.push(entry.sessionId);
      } catch {
        await updatePendingWorkout(entry.sessionId, { attempts: entry.attempts + 1 });
        failed.push(entry.sessionId);
      }
    }
    return { synced, failed };
  } finally {
    _syncing = false;
  }
}
